import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { useShowAdminId } from '@revelio/ui';

import {
  copyTextToClipboard,
  getDisplayIdFromNodeId,
} from '../SelectionListSelect/utils';
import styles from './AdminIdChip.module.css';

/**
 * The copyable `id: X` affordance shown on a selection row while the reveal is
 * active. Renders nothing when it isn't, so callers can drop it in
 * unconditionally.
 *
 * Shared by every app-side selection surface that isn't a tree node — the tree
 * has its own copy in `@revelio/ui`, which cannot import from here.
 */
export const AdminIdChip = ({ entityId }: { entityId: string }) => {
  const isRevealed = useShowAdminId();
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    },
    []
  );

  const displayId = getDisplayIdFromNodeId(entityId);
  if (!isRevealed || displayId.length === 0 || entityId.length === 0) {
    return null;
  }

  const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const copied = await copyTextToClipboard(displayId);
    if (!copied) return;
    setIsCopied(true);
    if (copyTimeoutRef.current !== null) {
      window.clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = window.setTimeout(() => setIsCopied(false), 1200);
  };

  return (
    <button
      type="button"
      className={classNames(styles.adminId, isCopied && styles.adminIdCopied)}
      onClick={(event) => void handleCopy(event)}
      title={`Copy id: ${displayId}`}
      data-copied={isCopied ? 'true' : 'false'}
    >
      {isCopied ? 'copied' : `id: ${displayId}`}
    </button>
  );
};
